import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Video, Link2, PhoneCall, Plus, X, Loader2, AlertTriangle, CalendarCheck2, Mail, Users,
  Settings2, ExternalLink,
} from "lucide-react";
import {
  TIMEZONES, DEFAULT_TIMEZONE, detectTimezone, wallTimeToUtcIso, utcIsoToWallTime,
  formatMeetingWindow, overlaps,
} from "../../lib/meetingTime";
import useMeetingConnect from "../../hooks/useMeetingConnect";

const DURATIONS = [15, 30, 45, 60, 90, 120];

/** Next half hour, in the given zone, as { date, time }. */
function defaultSlot(timezone) {
  const now = new Date();
  const rounded = new Date(Math.ceil(now.getTime() / 1800000) * 1800000 + 3600000);
  return utcIsoToWallTime(rounded.toISOString(), timezone);
}

const emptyAttendee = { name: "", email: "" };

export default function ScheduleMeetingDialog({
  open,
  onOpenChange,
  onScheduled,
  programId: lockedProgramId = null,
  programName: lockedProgramName = null,
  integrations = [],
  onIntegrationsChanged,
  meeting = null, // pass an existing meeting to edit/reschedule it
}) {
  const isEdit = Boolean(meeting?.id);
  const zoom = integrations.find((i) => i.provider === "zoom");
  const outlook = integrations.find((i) => i.provider === "microsoft");
  const zoomReady = Boolean(zoom?.connected && zoom?.status === "connected");
  const outlookReady = Boolean(outlook?.connected && outlook?.status === "connected");
  const { connect, connecting } = useMeetingConnect(() => onIntegrationsChanged?.());

  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [form, setForm] = useState({
    title: "",
    date: "",
    time: "",
    duration_minutes: 30,
    agenda: "",
    program_id: "",
    provider: "zoom",
    manual_join_url: "",
    location: "",
    send_invite_email: true,
    email_message: "",
    cc_host: true,
    add_to_calendar: true,
    calendar_invites_attendees: false,
  });
  const [attendees, setAttendees] = useState([]);
  const [draft, setDraft] = useState(emptyAttendee);
  const [programs, setPrograms] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [busyBlocks, setBusyBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const requestSeq = useRef(0);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // ---- open / reset -------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const tz = meeting.timezone || DEFAULT_TIMEZONE;
      const wall = utcIsoToWallTime(meeting.starts_at, tz);
      setTimezone(tz);
      setForm({
        title: meeting.title || "",
        date: wall.date,
        time: wall.time,
        duration_minutes: meeting.duration_minutes || 30,
        agenda: meeting.agenda || "",
        program_id: meeting.program_id || "",
        provider: meeting.provider || "zoom",
        manual_join_url: meeting.provider === "manual" ? meeting.join_url || "" : "",
        location: meeting.location || "",
        send_invite_email: true,
        email_message: "",
        cc_host: true,
        add_to_calendar: Boolean(meeting.calendar_event_id),
        calendar_invites_attendees: Boolean(meeting.calendar_invites_attendees),
      });
      setAttendees(meeting.attendees || []);
    } else {
      const tz = detectTimezone();
      const slot = defaultSlot(tz);
      setTimezone(tz);
      setForm({
        title: lockedProgramName ? `${lockedProgramName} — Call` : "",
        date: slot.date,
        time: slot.time,
        duration_minutes: 30,
        agenda: "",
        program_id: lockedProgramId || "",
        provider: zoomReady ? "zoom" : "manual",
        manual_join_url: "",
        location: "",
        send_invite_email: true,
        email_message: "",
        cc_host: true,
        add_to_calendar: outlookReady,
        calendar_invites_attendees: false,
      });
      setAttendees([]);
    }
    setDraft(emptyAttendee);
    setBusyBlocks([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meeting?.id]);

  // A connection made from inside the dialog should take effect immediately —
  // switch to Zoom (unless a link was already pasted) and turn the calendar on.
  useEffect(() => {
    if (!open || isEdit) return;
    if (zoomReady) {
      setForm((prev) =>
        prev.provider === "manual" && !prev.manual_join_url.trim() ? { ...prev, provider: "zoom" } : prev
      );
    }
    if (outlookReady) {
      setForm((prev) => (prev.add_to_calendar ? prev : { ...prev, add_to_calendar: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomReady, outlookReady, open]);

  // ---- programs for the picker -------------------------------------------
  useEffect(() => {
    if (!open || lockedProgramId) return;
    apiClient
      .get("/programs")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setPrograms(
          [...list].sort((a, b) => (a.program_name || "").localeCompare(b.program_name || ""))
        );
      })
      .catch(() => setPrograms([]));
  }, [open, lockedProgramId]);

  // ---- suggested attendees for the chosen program -------------------------
  const programId = form.program_id || lockedProgramId || "";
  useEffect(() => {
    if (!open || !programId) {
      setSuggested([]);
      return;
    }
    let cancelled = false;
    apiClient
      .get(`/meetings/program-contacts/${programId}`)
      .then((res) => {
        if (cancelled) return;
        const contacts = res.data?.contacts || [];
        setSuggested(contacts);
        // On a fresh booking, pre-fill the client contacts — that is the whole
        // point of picking a program.
        if (!isEdit) {
          setAttendees((prev) => (prev.length ? prev : contacts.map((c) => ({ name: c.name, email: c.email }))));
          setForm((prev) =>
            prev.title
              ? prev
              : { ...prev, title: `${res.data?.program_name || "Project"} — Call` }
          );
        }
      })
      .catch(() => setSuggested([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, programId]);

  // ---- conflict check ----------------------------------------------------
  const startIso = useMemo(
    () => wallTimeToUtcIso(form.date, form.time, timezone),
    [form.date, form.time, timezone]
  );

  const loadAvailability = useCallback(
    async (iso) => {
      if (!iso) return;
      const seq = ++requestSeq.current;
      const dayStart = new Date(iso);
      dayStart.setUTCHours(dayStart.getUTCHours() - 24);
      const dayEnd = new Date(iso);
      dayEnd.setUTCHours(dayEnd.getUTCHours() + 24);
      try {
        const res = await apiClient.get("/meetings/availability", {
          params: { start: dayStart.toISOString(), end: dayEnd.toISOString() },
        });
        if (seq === requestSeq.current) setBusyBlocks(res.data?.busy || []);
      } catch {
        if (seq === requestSeq.current) setBusyBlocks([]);
      }
    },
    []
  );

  useEffect(() => {
    if (!open || !startIso) return;
    const t = setTimeout(() => loadAvailability(startIso), 350);
    return () => clearTimeout(t);
  }, [open, startIso, loadAvailability]);

  const conflicts = useMemo(() => {
    if (!startIso) return [];
    return busyBlocks
      .filter((b) => b.status !== "free")
      .filter((b) => !isEdit || b.subject !== meeting?.title)
      .filter((b) => overlaps(startIso, form.duration_minutes, b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyBlocks, startIso, form.duration_minutes, isEdit]);

  const inPast = startIso ? new Date(startIso).getTime() < Date.now() : false;

  // ---- attendees ---------------------------------------------------------
  const addAttendee = (contact) => {
    const email = (contact.email || "").trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("That doesn't look like an email address");
      return;
    }
    if (attendees.some((a) => (a.email || "").toLowerCase() === email)) {
      toast.info("Already on the invite");
      return;
    }
    setAttendees((prev) => [...prev, { name: (contact.name || "").trim() || null, email }]);
    setDraft(emptyAttendee);
  };

  const removeAttendee = (email) =>
    setAttendees((prev) => prev.filter((a) => a.email !== email));

  const unusedSuggestions = suggested.filter(
    (c) => !attendees.some((a) => (a.email || "").toLowerCase() === c.email)
  );

  // ---- submit ------------------------------------------------------------
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Give the meeting a title");
    if (!startIso) return toast.error("Pick a date and time");
    if (form.provider === "zoom" && !zoomReady && !isEdit)
      return toast.error("Connect Zoom first, or paste a meeting link instead");
    if (form.provider === "manual" && !form.manual_join_url.trim())
      return toast.error("Paste the meeting link");
    if (form.send_invite_email && attendees.length === 0)
      return toast.error("Add at least one attendee, or turn the invite email off");

    setSaving(true);
    try {
      if (isEdit) {
        const res = await apiClient.patch(`/meetings/${meeting.id}`, {
          title: form.title.trim(),
          starts_at: startIso,
          duration_minutes: Number(form.duration_minutes),
          timezone,
          agenda: form.agenda,
          attendees,
          location: form.location,
          ...(form.provider === "manual" ? { manual_join_url: form.manual_join_url.trim() } : {}),
          notify: form.send_invite_email,
          email_message: form.email_message || null,
        });
        (res.data?.warnings || []).forEach((w) => toast.warning(w));
        toast.success(res.data?.notified ? "Meeting updated — attendees notified" : "Meeting updated");
        onScheduled?.(res.data?.meeting);
      } else {
        const res = await apiClient.post("/meetings", {
          title: form.title.trim(),
          starts_at: startIso,
          duration_minutes: Number(form.duration_minutes),
          timezone,
          agenda: form.agenda || null,
          attendees,
          program_id: programId || null,
          provider: form.provider,
          manual_join_url: form.provider === "manual" ? form.manual_join_url.trim() : null,
          location: form.provider === "none" ? form.location || null : null,
          send_invite_email: form.send_invite_email,
          email_message: form.email_message || null,
          cc_host: form.cc_host,
          add_to_calendar: form.add_to_calendar,
          calendar_invites_attendees: form.calendar_invites_attendees,
        });
        (res.data?.warnings || []).forEach((w) => toast.warning(w));
        // Report what actually happened rather than what we asked for — the send
        // can be skipped (email not configured) or fail on its own.
        const emailStatus = res.data?.meeting?.email_status;
        if (emailStatus === "sent") toast.success("Meeting booked and invite sent");
        else if (emailStatus === "skipped")
          toast.success("Meeting booked — invite not sent, email isn't set up on this server");
        else if (emailStatus === "failed") toast.success("Meeting booked, but the invite email failed");
        else toast.success("Meeting booked");
        onScheduled?.(res.data?.meeting);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the meeting");
    } finally {
      setSaving(false);
    }
  };

  const providerOptions = [
    { id: "zoom", label: "Zoom", icon: Video, hint: zoomReady ? "Created on your account" : "Connect Zoom first", disabled: !zoomReady },
    { id: "manual", label: "Paste a link", icon: Link2, hint: "Teams, Meet, or an existing Zoom link", disabled: false },
    { id: "none", label: "No link", icon: PhoneCall, hint: "Phone or in person", disabled: false },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="schedule-meeting-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Reschedule meeting" : "Book a meeting"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Changes are pushed to Zoom and your calendar, and attendees get an updated invite."
              : "HQ creates the meeting, adds it to your calendar and emails the client an invite — one step."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Project */}
          {!lockedProgramId && (
            <div>
              <Label className="label-text">Project (optional)</Label>
              <Select
                value={form.program_id || "none"}
                onValueChange={(v) => set({ program_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="meeting-program">
                  <SelectValue placeholder="Not tied to a project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not tied to a project</SelectItem>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.program_name}
                      {p.client_name ? ` · ${p.client_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Linking a project pulls in the client's contacts and files the invite in the
                project's comms history.
              </p>
            </div>
          )}
          {lockedProgramId && lockedProgramName && (
            <p className="text-sm text-muted-foreground">
              Meeting for <span className="text-foreground">{lockedProgramName}</span>
            </p>
          )}

          {/* Title */}
          <div>
            <Label className="label-text">Title</Label>
            <Input
              className="mt-1.5"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Kickoff call"
              data-testid="meeting-title"
            />
          </div>

          {/* When */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="label-text">Date</Label>
              <Input
                type="date"
                className="mt-1.5"
                value={form.date}
                onChange={(e) => set({ date: e.target.value })}
                data-testid="meeting-date"
              />
            </div>
            <div>
              <Label className="label-text">Time</Label>
              <Input
                type="time"
                className="mt-1.5"
                value={form.time}
                onChange={(e) => set({ time: e.target.value })}
                data-testid="meeting-time"
              />
            </div>
            <div>
              <Label className="label-text">Length</Label>
              <Select
                value={String(form.duration_minutes)}
                onValueChange={(v) => set({ duration_minutes: Number(v) })}
              >
                <SelectTrigger className="mt-1.5" data-testid="meeting-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d < 60 ? `${d} min` : d === 60 ? "1 hour" : `${d / 60} hours`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-text">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="mt-1.5" data-testid="meeting-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {startIso && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="meeting-when-preview">
                {formatMeetingWindow(startIso, form.duration_minutes, timezone, { withYear: true })}
              </p>
            )}
          </div>

          {inPast && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              That time is in the past.
            </div>
          )}
          {conflicts.length > 0 && (
            <div
              className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5"
              data-testid="meeting-conflict"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <p>You're already busy then:</p>
                <ul className="mt-1 space-y-0.5">
                  {conflicts.slice(0, 3).map((c, i) => (
                    <li key={i}>
                      · {c.subject || "Busy"} ({c.source === "hq" ? "HQ" : "Outlook"})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Conferencing */}
          {!isEdit && (
            <div>
              <Label className="label-text">How you'll meet</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5">
                {providerOptions.map((opt) => {
                  const Icon = opt.icon;
                  const active = form.provider === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={opt.disabled}
                      onClick={() => set({ provider: opt.id })}
                      data-testid={`meeting-provider-${opt.id}`}
                      className={`text-left border rounded-md p-3 transition-colors ${
                        active ? "border-foreground bg-secondary/60" : "border-border hover:bg-secondary/30"
                      } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span className="flex items-center gap-2 text-sm text-foreground">
                        <Icon className="w-4 h-4" />
                        {opt.label}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-1">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>

              {/* One-click setup so nobody has to go hunting through Settings */}
              {!zoomReady && (
                <div
                  className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 p-2.5 rounded-md bg-secondary/50 border border-border"
                  data-testid="zoom-connect-nudge"
                >
                  <p className="text-xs text-muted-foreground flex-1">
                    {zoom?.configured === false
                      ? "Zoom isn't set up on this server yet — ask an admin to add the Zoom app credentials."
                      : "Connect Zoom once and HQ creates the link for you every time. Opens in a small window — your draft stays put."}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={zoom?.configured === false || connecting === "zoom"}
                    onClick={() => connect("zoom")}
                    data-testid="connect-zoom-inline"
                  >
                    {connecting === "zoom" ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Video className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Connect Zoom
                  </Button>
                </div>
              )}
            </div>
          )}

          {form.provider === "manual" && (
            <div>
              <Label className="label-text">Meeting link</Label>
              <Input
                className="mt-1.5"
                value={form.manual_join_url}
                onChange={(e) => set({ manual_join_url: e.target.value })}
                placeholder="https://us02web.zoom.us/j/…"
                data-testid="meeting-manual-link"
              />
            </div>
          )}
          {form.provider === "none" && !isEdit && (
            <div>
              <Label className="label-text">Where</Label>
              <Input
                className="mt-1.5"
                value={form.location}
                onChange={(e) => set({ location: e.target.value })}
                placeholder="Phone call — we'll dial you"
              />
            </div>
          )}

          {/* Attendees */}
          <div>
            <Label className="label-text flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Attendees
            </Label>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="meeting-attendees">
                {attendees.map((a) => (
                  <span
                    key={a.email}
                    className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-sm"
                  >
                    <span className="text-foreground">{a.name || a.email}</span>
                    {a.name && <span className="text-muted-foreground text-xs">{a.email}</span>}
                    <button
                      type="button"
                      onClick={() => removeAttendee(a.email)}
                      className="p-0.5 rounded-full hover:bg-background"
                      aria-label={`Remove ${a.email}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {unusedSuggestions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground">From this project:</span>
                {unusedSuggestions.map((c) => (
                  <button
                    key={c.email}
                    type="button"
                    onClick={() => addAttendee(c)}
                    className="inline-flex items-center gap-1 text-xs border border-dashed border-border rounded-full px-2.5 py-1 hover:bg-secondary/50"
                  >
                    <Plus className="w-3 h-3" />
                    {c.name || c.email}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Input
                placeholder="Name (optional)"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className="sm:w-40"
              />
              <Input
                placeholder="email@client.com"
                value={draft.email}
                type="email"
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAttendee(draft);
                  }
                }}
                className="flex-1"
                data-testid="meeting-attendee-email"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addAttendee(draft)}
                data-testid="meeting-add-attendee"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Agenda */}
          <div>
            <Label className="label-text">Agenda (optional)</Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              value={form.agenda}
              onChange={(e) => set({ agenda: e.target.value })}
              placeholder="What we'll cover — shown in the invite and on the calendar entry."
            />
          </div>

          {/* Delivery */}
          <div className="border-t border-border pt-4 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={form.send_invite_email}
                onCheckedChange={(v) => set({ send_invite_email: Boolean(v) })}
                data-testid="meeting-send-email"
              />
              <span>
                <span className="text-sm text-foreground flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {isEdit ? "Email attendees about the change" : "Email the invite now"}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Branded invite with the link and a calendar attachment, reply-to you.
                </span>
              </span>
            </label>

            {form.send_invite_email && (
              <>
                <Textarea
                  rows={2}
                  value={form.email_message}
                  onChange={(e) => set({ email_message: e.target.value })}
                  placeholder="Optional note to open the email — e.g. “Looking forward to walking you through the V1 renders.”"
                  data-testid="meeting-email-message"
                />
                {!isEdit && (
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <Checkbox
                      checked={form.cc_host}
                      onCheckedChange={(v) => set({ cc_host: Boolean(v) })}
                    />
                    <span className="text-sm text-muted-foreground">Copy me on the invite</span>
                  </label>
                )}
              </>
            )}

            {/* Outlook is optional, so when the server has no Microsoft app at all
                this row is hidden rather than shown as a dead checkbox — booking
                works fine without it. It appears once Outlook is switched on. */}
            {!isEdit && outlook && outlook.configured !== false && (
              <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                <label
                  className={`flex items-start gap-2.5 flex-1 ${outlookReady ? "cursor-pointer" : "opacity-70"}`}
                >
                  <Checkbox
                    checked={form.add_to_calendar && outlookReady}
                    disabled={!outlookReady}
                    onCheckedChange={(v) => set({ add_to_calendar: Boolean(v) })}
                    data-testid="meeting-add-calendar"
                  />
                  <span>
                    <span className="text-sm text-foreground flex items-center gap-1.5">
                      <CalendarCheck2 className="w-3.5 h-3.5" /> Add to my Outlook calendar
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {outlookReady
                        ? "Blocks the time on your calendar."
                        : "Not connected yet — connect it right here."}
                    </span>
                  </span>
                </label>
                {!outlookReady && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={connecting === "microsoft"}
                    onClick={() => connect("microsoft")}
                    data-testid="connect-outlook-inline"
                    className="shrink-0"
                  >
                    {connecting === "microsoft" ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <CalendarCheck2 className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Connect Outlook
                  </Button>
                )}
              </div>
            )}

            {!isEdit && outlookReady && form.add_to_calendar && (
              <label className="flex items-start gap-2.5 cursor-pointer pl-6">
                <Checkbox
                  checked={form.calendar_invites_attendees}
                  onCheckedChange={(v) => set({ calendar_invites_attendees: Boolean(v) })}
                />
                <span>
                  <span className="text-sm text-foreground">Send the calendar invite from Outlook too</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Attendees get a native Outlook invite they can accept. Leave off to avoid
                    two emails.
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-between gap-3">
          <Link
            to="/meetings?tab=setup"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="manage-connections-link"
          >
            <Settings2 className="w-3.5 h-3.5" />
            Manage Zoom &amp; Outlook connections
            <ExternalLink className="w-3 h-3" />
          </Link>
          <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="meeting-submit">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : form.send_invite_email ? (
              "Book and send invite"
            ) : (
              "Book meeting"
            )}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
